import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import PdfPageCanvas from './PdfPageCanvas'

const PagePlaceholder = styled.div`
  position: relative;
  flex: 0 0 auto;
`

export function LazyPdfPage({ page, pageIndex, scale, active, scrollRoot, setPageRef, renderOverlay, testId = 'pdf-page' }) {
  const [shouldRender, setShouldRender] = useState(active)
  const wrapperRef = useRef(null)
  const viewport = page.getViewport({ scale })

  useEffect(() => {
    if (!setPageRef) return undefined
    setPageRef(pageIndex, wrapperRef.current)
    return () => setPageRef(pageIndex, null)
  }, [pageIndex, setPageRef])

  useEffect(() => {
    if (active) setShouldRender(true)
  }, [active])

  useEffect(() => {
    const element = wrapperRef.current
    if (shouldRender || !element || !scrollRoot || typeof IntersectionObserver === 'undefined') return undefined

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldRender(true)
          observer.disconnect()
        }
      },
      { root: scrollRoot, rootMargin: '100% 0px' }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [scrollRoot, shouldRender])

  return (
    <PagePlaceholder
      ref={wrapperRef}
      data-testid="pdf-page-placeholder"
      data-page-number={pageIndex + 1}
      style={{ width: viewport.width, height: viewport.height }}>
      {shouldRender && <PdfPageCanvas page={page} scale={scale} renderOverlay={dims => renderOverlay?.(pageIndex, dims)} testId={testId} />}
    </PagePlaceholder>
  )
}

LazyPdfPage.propTypes = {
  page: PropTypes.object.isRequired,
  pageIndex: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  scrollRoot: PropTypes.object,
  setPageRef: PropTypes.func,
  renderOverlay: PropTypes.func,
  testId: PropTypes.string
}

// Lazily render PDF pages in one continuous scroll surface.
function ContinuousPdfPages({ pages, scale, pageIndex, navigationKey = 0, scrollRef, onPageChange = () => {}, renderOverlay }) {
  const pageRefs = useRef(new Map())
  const [scrollRoot, setScrollRoot] = useState(null)
  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange

  useEffect(() => {
    setScrollRoot(scrollRef.current)
  }, [scrollRef, pages.length])

  useEffect(() => {
    const root = scrollRef.current
    const target = pageRefs.current.get(pageIndex)
    if (!root || !target) return
    target.scrollIntoView?.({ block: 'start' })
  }, [navigationKey, pages.length, scale])

  useEffect(() => {
    const root = scrollRef.current
    if (!root) return undefined
    let frame = null

    const updatePage = () => {
      frame = null
      const focusY = root.getBoundingClientRect().top + Math.min(root.clientHeight * 0.35, 240)
      let closestIndex = 0
      let closestDistance = Number.POSITIVE_INFINITY

      pageRefs.current.forEach((element, index) => {
        if (!element) return
        const rect = element.getBoundingClientRect()
        const distance = focusY < rect.top ? rect.top - focusY : focusY > rect.bottom ? focusY - rect.bottom : 0
        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      })
      onPageChangeRef.current(closestIndex)
    }

    const handleScroll = () => {
      if (frame !== null) return
      frame = requestAnimationFrame(updatePage)
    }

    root.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      root.removeEventListener('scroll', handleScroll)
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [scrollRef, pages.length])

  const setPageRef = useCallback((index, element) => {
    if (element) pageRefs.current.set(index, element)
    else pageRefs.current.delete(index)
  }, [])

  return pages.map((pdfPage, index) => (
    <LazyPdfPage
      key={pdfPage.pageNumber ?? index}
      page={pdfPage}
      pageIndex={index}
      scale={scale}
      active={index === pageIndex}
      scrollRoot={scrollRoot}
      setPageRef={setPageRef}
      renderOverlay={renderOverlay}
    />
  ))
}

ContinuousPdfPages.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
  scale: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  navigationKey: PropTypes.number,
  scrollRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  onPageChange: PropTypes.func,
  renderOverlay: PropTypes.func
}

export default ContinuousPdfPages
